import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import styles from "./trend-line-widget.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import ReactEChart from "echarts-for-react";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitLabelOf } from "@src/cloud-connect/units/utils/unit-label-of";
import { useWidgetTimeseries } from "../use-widget-timeseries";
import { isoToDurationMs } from "../constants";
import { buildTrendLineOption } from "./build-trend-line-option";
import { useThemeColorsForCharts } from "../use-theme-colors-for-charts";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import Spinner from "@src/components/atoms/spinner/spinner";
import { OPCUAGaugeSemantics, WidgetSemantics } from "@src/types/dashboards";
import { getWidgetDataSource, getWidgetWindow } from "../widget-query";
import {
  applyScaleToBounds,
  applyScaleToNumbers,
} from "../utils/value-scaling";
import { readAxisBounds } from "../axis-bounds";

const cssClassName = "trend-line-widget";

/** Mocked preview series (~68% of range, like the gauge placeholder). */
const buildMockSeries = (semantics: WidgetSemantics) => {
  const s = semantics as Partial<OPCUAGaugeSemantics>;
  const min = s.min_value ?? 0;
  const max = s.max_value ?? 100;
  const base = min + 0.68 * (max - min);
  const span = (max - min) * 0.08;
  const now = Date.now();
  const step = 10 * 60 * 1000; // 10 min
  const times: string[] = [];
  const values: number[] = [];
  for (let i = 0; i < 8; i++) {
    times.push(new Date(now - (7 - i) * step).toISOString());
    values.push(Math.round((base + Math.sin(i) * span) * 100) / 100);
  }
  return { times, values };
};

const getUnitLabel = (
  presentation: WidgetRendererProps["presentation"],
  units: ReturnType<typeof selectUnitsById>,
) => unitLabelOf(getUnitById(units, presentation?.unit));

/**
 * echarts sizes at mount; the tile grid lays out asynchronously (0x0 at first
 * render, dashboard switches, drag-resize) — observe the wrapper and resize
 * the canvas whenever the container settles, or the chart stays shrunk.
 * Own effect: runs on the chart's own mount (a parent effect would fire while
 * the live branch still shows the spinner) and disconnects on unmount.
 */
// Node id/name lives in the echarts legend (top-left, auto-colored icon);
// only the unit label needs a custom overlay (top-right, unit id resolved via
// the slice).
const ResizeAwareChart = ({
  option,
  unitLabel,
}: {
  option: any;
  /** Persistent unit label rendered top-right (unit id resolved via the slice). */
  unitLabel?: string;
}) => {
  const echartsRef = useRef<ReactEChart>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const resize = () =>
      echartsRef.current
        ?.getEchartsInstance()
        ?.resize({ width: "auto", height: "auto" });
    let lastW = -1;
    let lastH = -1;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {
        width: 0,
        height: 0,
      };
      if (width === lastW && height === lastH) return; // no real change
      lastW = width;
      lastH = height;
      // Defer to the next frame: resizing from inside the RO callback
      // re-flows the observed element and trips the browser's "ResizeObserver
      // loop completed with undelivered notifications" warning.
      requestAnimationFrame(resize);
    });
    observer.observe(el);
    // Observes the closest .grid-stack-item ancestor, because when expanding and collapsing
    // the left and right columns charts should rerender to not be cropped.
    const tileEl = el.closest(".grid-stack-item");
    if (tileEl && tileEl !== el) observer.observe(tileEl);
    // One RAF after paint with the layout settled: covers echarts initializing
    // before the container had its final width (live charts mount after the
    // fetch/spinner, so init can land mid-layout while the RO callback timing
    // is less reliable than the sync mock mount).
    const raf = requestAnimationFrame(resize);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={wrapperRef} className={styles[`${cssClassName}__chart`]}>
      {unitLabel ? (
        <span className={styles[`${cssClassName}__unit`]}>{unitLabel}</span>
      ) : null}
      <ReactEChart
        ref={echartsRef}
        style={{ width: "100%", height: "100%" }}
        option={option}
      />
    </div>
  );
};

export const TrendLineWidget = ({
  title,
  semantics,
  presentation,
  encoding,
  query,
  preview = false,
  showTitle = true,
  // Off-screen tile: ticks skip fetching; the interval keeps its schedule;
  // re-entry refetches once via the visibility bridge.
  inViewport = true,
}: WidgetRendererProps) => {
  const s = semantics as Partial<
    OPCUAGaugeSemantics & { thresholds?: unknown }
  >;
  const thresholds = s.thresholds as
    | { warning?: number | null; critical?: number | null }
    | null
    | undefined;

  const series = useWidgetTimeseries(preview ? undefined : query, {
    frequency: query?.sampling?.frequency ?? "raw",
    // Relative window (duration) rolls at render; absolute from/to for timeframe.
    // `getWidgetWindow` reads `from ?? from_` (BE alias for the reserved word).
    durationMs: isoToDurationMs(getWidgetWindow(query)?.duration),
    from: getWidgetWindow(query)?.from,
    to: getWidgetWindow(query)?.to,
    preview,
    inViewport,
  });

  // Aggregated-line display toggles from semantics. Only meaningful when
  // the chart is actually aggregated (raw mode has no v_min/v_max to draw);
  // we still pass the values to the option builder — it no-ops on raw.
  const semForBounds = semantics as Partial<
    OPCUAGaugeSemantics & { show_min?: boolean; show_max?: boolean }
  >;
  const showMin = semForBounds.show_min === true;
  const showMax = semForBounds.show_max === true;

  const units = useSelector(selectUnitsById);
  const unitLabel = getUnitLabel(presentation, units);
  // The drawer's prefill picks a random palette hex on first save when no
  // explicit color is set, and that pick is persisted to `presentation.color`.
  // Treating absence as the default keeps render-time deterministic and
  // matches whatever the drawer decides to write.
  const lineColor = presentation?.color ?? undefined;

  // Mock + static: picker preview (preview prop) AND freshly-added widgets
  // (no query binding yet — template templates are unbound until configured).
  const showMock = preview || !query;
  // Drives the echarts legend (top-left, auto-colored icon + name). It labels
  // the series with the node it reads: the node's display name, else its id.
  // The widget title is never used here — the tile header already carries it —
  // and a mocked/unbound tile has no node at all, so it gets no legend.
  const nodeLabel = showMock
    ? ""
    : series.nodeName || getWidgetDataSource(query)?.source || "";

  // Resolved theme colours for chart text — see
  // `useThemeColorsForCharts`. Recomputes when the picker drag-stops
  // update the central-panel CSS vars, so a live theme change
  // re-builds the echarts options and the canvas repaints in the
  // new pigment without a full data refresh.
  // `preview` = picker tile: fixed dark chrome, so no dashboard palette.
  const themeColors = useThemeColorsForCharts(preview);

  const option = useMemo(() => {
    // Manual Y bounds apply to the preview/mock chart too, not just live data.
    const axis = readAxisBounds(semantics);
    const yMin = axis?.min ?? null;
    const yMax = axis?.max ?? null;
    if (showMock) {
      const mock = buildMockSeries(semantics);
      return buildTrendLineOption({
        times: mock.times,
        values: mock.values,
        nodeName: nodeLabel,
        unitLabel,
        lineColor,
        yMin,
        yMax,
        static: true,
        themeColors,
      });
    }
    // Scale the fetched series before the option is built; raw data stays
    // untouched in the hook. Bounds go through `applyScaleToBounds` so a
    // negative factor keeps min ≤ max.
    const bounds = applyScaleToBounds(
      "value",
      series.minValues,
      series.maxValues,
      encoding?.transforms,
    );
    return buildTrendLineOption({
      times: series.times,
      values: applyScaleToNumbers("value", series.values, encoding?.transforms),
      // The hook returns undefined for raw mode, so the option
      // builder no-ops on min/max sub-series — safe to forward
      // unconditionally on aggregate paths.
      minValues: bounds.minValues,
      maxValues: bounds.maxValues,
      yMin,
      yMax,
      showMin,
      showMax,
      nodeName: nodeLabel,
      breadcrumbs: series.breadcrumbs,
      unitLabel,
      thresholds,
      lineColor,
      themeColors,
    });
  }, [
    showMock,
    semantics,
    title,
    presentation,
    encoding,
    query,
    series,
    thresholds,
    units,
    nodeLabel,
    unitLabel,
    lineColor,
    showMin,
    showMax,
    themeColors,
  ]);

  const body = showMock ? (
    <ResizeAwareChart option={option} unitLabel={unitLabel} />
  ) : series.isLoading ? (
    <div className={styles[`${cssClassName}__chart`]}>
      <Spinner />
    </div>
  ) : series.hasNoData || series.times.length === 0 ? (
    <NoDataInTimeframe />
  ) : (
    <ResizeAwareChart option={option} unitLabel={unitLabel} />
  );

  return (
    <div className={styles[cssClassName]}>
      {showTitle && (
        <span className={styles[`${cssClassName}__title`]}>
          {title || "Trend Line"}
        </span>
      )}
      {body}
    </div>
  );
};
